import isFunction from 'lodash/isFunction';
import styled, { css } from 'styled-components/macro';
import { colorAccentError700, colorAccentError800Base, colorAccentSuccess800, colorBrandYamahaRed, colorCoreBlack200, colorCoreBlack300, colorCoreBlack900Base, colorCoreGray1000Base, colorCoreGray500, colorCoreGray800, } from '@yme/tokens/colors';
import { CORNER_RADIUS } from '@yme/tokens/cornerRadius';
import { fontWeightDefault, fontWeightMedium, lineHeightXS, lineHeightXXS, } from '@yme/tokens/typography';
import { ellipsisEndingLine, getTextStyles } from '@yme/utils/styles';
import { INPUT_STATE_ERROR, INPUT_STATE_SUCCESS } from './constants';
export const InfoTextStyled = styled.span `
  display: block;
  margin-top: 10px;
  ${fontWeightDefault};
  ${lineHeightXXS};
  font-size: 12px;

  color: ${({ state }) => {
    switch (state) {
        case INPUT_STATE_SUCCESS:
            return colorAccentSuccess800;
        case INPUT_STATE_ERROR:
            return colorAccentError800Base;
        default:
            return colorCoreBlack900Base;
    }
}};
`;
export const LabelStyled = styled.label `
  display: inline-block;
  margin-bottom: 8px;
  color: ${colorCoreBlack900Base};

  ${fontWeightMedium};
  ${lineHeightXS};

  ${({ isRequired }) => isRequired &&
    `
    &::after {
      color: ${colorBrandYamahaRed};
    }   
    &::after {
      content: "*";     
    }`};

  ${({ isRequired, isRightToLeft }) => isRequired &&
    (isRightToLeft ? '&::after {  margin-right: 2px; }' : '&::after {  margin-left: 2px; }')};
`;
export const InputContainer = styled.div `
  display: block;

  ${getTextStyles('Ubuntu', 400, 14, 20)}

  ${({ isRightToLeft }) => isRightToLeft && 'direction: rtl;'}

  ${({ isDisabled }) => isDisabled &&
    css `
      ${InfoTextStyled},
      ${LabelStyled} {
        color: ${colorCoreBlack300};
      }
    `};

  ${({ isFullWidth }) => isFullWidth && 'width: 100%;'}
`;
const getStylesForIcon = (isLeft, icon) => {
    const position = isLeft ? 'before' : 'after';
    const offsetProp = isLeft ? 'left' : 'right';
    const content = icon ? 'content: ""' : '';
    return isFunction(icon)
        ? css `
        &::${position} {
          content: '${icon?.()}';
          ${offsetProp}: 12px;
        }
      `
        : css `
        &::${position} {
          ${content};
          ${offsetProp}: 12px;
          background-image: url(${icon});
        }
      `;
};
export const InputWrapper = styled.div `
  position: relative;

  &::before,
  &::after {
    position: absolute;
    top: 50%;
    display: block;
    width: 20px;
    height: 20px;
    line-height: 20px;
    text-align: right;
    background-size: contain;
    background-position: center;
    background-repeat: no-repeat;
    transform: translateY(-50%);
  }

  ${({ leftIcon }) => getStylesForIcon(true, leftIcon)}

  ${({ rightIcon }) => getStylesForIcon(false, rightIcon)}

  ${({ isDisabled }) => isDisabled &&
    css `
      &::before,
      &::after {
        opacity: 0.6;
      }
    `};
`;
function getInputPaddings({ customPadding, isRightIcon, isLeftIcon, }) {
    if (isRightIcon && isLeftIcon) {
        return '12px 40px 12px 40px';
    }
    if (isRightIcon) {
        return '12px 40px 12px 16px';
    }
    if (isLeftIcon) {
        return '12px 16px 12px 40px';
    }
    return `12px 16px 12px ${customPadding + 16}px`;
}
export const InputStyled = styled.input `
  padding: ${({ isRightIcon, isLeftIcon, customPadding }) => getInputPaddings({
    customPadding,
    isRightIcon,
    isLeftIcon,
})};

  ${getTextStyles('Ubuntu', 400, 14, 20)}

  box-shadow: 0 0 0 1px ${({ state }) => state === INPUT_STATE_ERROR ? colorAccentError700 : colorCoreGray500};

  border-radius: ${CORNER_RADIUS.s};
  width: 100%;
  box-sizing: border-box;
  border: none;
  -webkit-appearance: none;
  ${({ isTextarea }) => !isTextarea && ellipsisEndingLine}
  &:hover:not(:disabled),
  &:focus:not(:disabled) {
    box-shadow: 0 0 0 1px ${colorCoreGray800};
  }

  &:hover {
    outline: none;
  }

  &:focus {
    box-shadow: ${({ isInputChanged }) => isInputChanged && `0 0 0 1px ${colorCoreBlack900Base}`};
  }

  &::placeholder {
    color: ${colorCoreBlack300};
  }

  &:disabled {
    background-color: transparent;
    border-color: ${colorCoreGray500};
    color: ${colorCoreGray1000Base};
    &::placeholder {
      color: ${colorCoreBlack200};
    }
  }
  &:-webkit-autofill {
    -webkit-box-shadow: 0 0 0 1000px white inset;
  }
  &::-ms-reveal,
  &::-ms-clear {
    display: none;
  }
`;
